import { Input } from 'antd'
import type { TextAreaRef } from 'antd/es/input/TextArea'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'

import type { TeamMember } from 'features/TeamChat/api/teamChat.types'
import styles from 'features/TeamChat/teamChat.module.scss'

import { MentionBar } from './chat/MentionBar'
import { insertHandle, matchMembers, openToken } from './chat/mentionChips'
import { SendIcon } from './TeamIcons'

type Props = {
  id: string
  value: string
  members: TeamMember[]
  placeholder: string
  ariaLabel: string
  sendLabel: string
  maxLength: number
  error: string
  busy?: boolean
  helpBusy: boolean
  focusOnOpen?: boolean
  // Shown between the mention bar and the message box (the chat's "also create a request").
  extra?: ReactNode
  onChange: (value: string) => void
  onSubmit: () => void
  onHelp: () => void
  onCancel?: () => void
}

// One composer for chat and new requests: mention chips, help, a pill message box and send.
export const TeamComposer = ({
  id,
  value,
  members,
  placeholder,
  ariaLabel,
  sendLabel,
  maxLength,
  error,
  busy = false,
  helpBusy,
  focusOnOpen,
  extra,
  onChange,
  onSubmit,
  onHelp,
  onCancel,
}: Props) => {
  const [caret, setCaret] = useState(0)
  const [barOpen, setBarOpen] = useState(false)
  const [dismissedAt, setDismissedAt] = useState<number | null>(null)
  const inputRef = useRef<TextAreaRef>(null)
  const nextCaret = useRef<number | null>(null)

  // Typing "@" opens the same chips as the Mention button, filtered by what follows it.
  const token = openToken(value, caret)
  const typing = token !== null && token.start !== dismissedAt
  const chips = matchMembers(members, typing ? token.query : '')
  const chipsOpen = (barOpen || typing) && chips.length > 0

  // A form opened on purpose (New request) puts the caret in its box right away.
  useEffect(() => {
    if (focusOnOpen) inputRef.current?.focus()
  }, [focusOnOpen])

  useLayoutEffect(() => {
    const el = inputRef.current?.resizableTextArea?.textArea
    if (!el || nextCaret.current === null) return
    el.focus()
    el.setSelectionRange(nextCaret.current, nextCaret.current)
    nextCaret.current = null
  }, [value])

  const pick = (m: TeamMember) => {
    const edit = insertHandle(value, caret, m.handle)
    nextCaret.current = edit.caret
    setCaret(edit.caret)
    setBarOpen(false)
    onChange(edit.text)
  }

  const submit = () => {
    setBarOpen(false)
    onSubmit()
  }

  const help = () => {
    setBarOpen(false)
    onHelp()
  }

  // Enter completes a name being typed, otherwise sends; Escape closes open chips before the panel.
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape' && chipsOpen) {
      e.preventDefault()
      setBarOpen(false)
      if (token) setDismissedAt(token.start)
      return
    }
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return
    e.preventDefault()
    if (typing && chips.length > 0) return pick(chips[0])
    submit()
  }

  return (
    <>
      <MentionBar
        open={chipsOpen}
        chips={chips}
        helpBusy={helpBusy}
        cancelDisabled={busy}
        onToggle={() => setBarOpen(!chipsOpen)}
        onPick={pick}
        onHelp={help}
        onCancel={onCancel}
      />
      {extra}
      <div className={`${styles.footer} ${styles.footerFlush}`}>
        <div className={`${styles.row} ${styles.composer}`}>
          <div className={styles.grow}>
            <Input.TextArea
              ref={inputRef}
              id={id}
              className={styles.chatInput}
              aria-label={ariaLabel}
              value={value}
              maxLength={maxLength}
              placeholder={placeholder}
              autoSize={{ minRows: 1, maxRows: 4 }}
              onChange={(e) => {
                setCaret(e.target.selectionStart ?? e.target.value.length)
                onChange(e.target.value)
              }}
              onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
              onKeyDown={onKeyDown}
            />
          </div>
          <button
            type='button'
            className={styles.sendButton}
            aria-label={sendLabel}
            aria-busy={busy || undefined}
            disabled={busy || !value.trim()}
            onClick={submit}>
            <SendIcon />
          </button>
        </div>
        <div role='alert' className={styles.error}>
          {error}
        </div>
      </div>
    </>
  )
}
